import {Outlet, Navigate, Routes, Route} from 'react-router-dom';
import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {observer} from 'mobx-react';
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import NotificationList from './NotificationList.js';
import SearchDialog from './search/SearchDialog';
import Notifications from './Notifications.js';
import {OE_AUTH, unixNow} from './Util.js';
import {useNavigate} from 'react-router';
import LoadSplash from './LoadSplash.js';
import AboutDialog from './AboutDialog';
import WsR2, {COMM_OK} from './WsR2.js';
import Taxons from './taxons/Taxons.js';
import Locations from './Locations.js';
import PwdDialog from './PwdDialog.js';
import UserList from './UserList.js';
import TripList from './TripList.js';
import DestList from './DestList.js';
import Settings from './Settings.js';
import Calendar from './Calendar.js';
import Stats from './stats/Stats.js';
import ListMap from './ListMap.js';
import ObsMain from './Main.js';
import Login from './Login.js';
import ObsAdd from './Add.js';
import BWTF from './BWTF.js';
import Navi from './Navi.js';
import './App.css';

const ProtectedRoute = () => {
	const gStorage = useGlobalStorage();

	return gStorage.conn && gStorage.conn.is_auth ? <Outlet /> : <Navigate to='/login' />
};

const App = observer(() => {
	const [initDone, setInitDone] = useState(false);
	const {i18n} = useTranslation('obs');
	const gStorage = useGlobalStorage();
	const navigate = useNavigate();

	useEffect(() => {
		init();

		return () => {
			let gpsID;

			gpsID = gStorage.gps.id;

			navigator.geolocation.clearWatch(gpsID);
			gStorage.gps.setID(0);
		}
		// eslint-disable-next-line
	}, []);

	/* ------------------------------------------------------------------ */

	function initLists() {
		let s_speclist = window.localStorage.getItem('speclist');
		if (!s_speclist)
			s_speclist = JSON.stringify({last_upd: 0, list: []});

		window.localStorage.removeItem('triplist');
		window.localStorage.removeItem('id');
		window.localStorage.removeItem('userid');
		window.localStorage.removeItem('sexlist');
		window.localStorage.removeItem('agelist');
		window.localStorage.removeItem('plumagelist');
		window.localStorage.removeItem('statelist');
		window.localStorage.removeItem('munilist');

		let s_citylist = window.localStorage.getItem('citylist');
		if (!s_citylist)
			s_citylist = JSON.stringify({last_upd: 0, list: []});

		let s_reglist = window.localStorage.getItem('reglist');
		if (!s_reglist)
			s_reglist = JSON.stringify({last_upd: 0, list: []});

		let s_assolist = window.localStorage.getItem('assolist');
		if (!s_assolist)
			s_assolist = JSON.stringify({last_upd: 0, list: []});

		let s_countrylist = window.localStorage.getItem('countrylist');
		if (!s_countrylist)
			s_countrylist = JSON.stringify({last_upd: 0, list: []});

		gStorage.lists.setSpecList(JSON.parse(s_speclist));
		gStorage.lists.setCityList(JSON.parse(s_citylist));
		gStorage.lists.setRegList(JSON.parse(s_reglist));
		gStorage.lists.setAssoList(JSON.parse(s_assolist));
		gStorage.lists.setCountryList(JSON.parse(s_countrylist));

		/* Initialize 'dynamic' lists (offline use) */
		if (!gStorage.lists.triplist || !gStorage.lists.triplist.list || gStorage.lists.triplist.list.length <= 0) {
			gStorage.lists.setTripList({last_upd: unixNow(), list:[]});
		}
		if (!gStorage.lists.userlist || !gStorage.lists.userlist.list || gStorage.lists.userlist.list.length <= 0) {
			gStorage.lists.setUserList({last_upd: unixNow(), list:[]});
		}
	}

	/* ------------------------------------------------------------------ */

	const connMeasRxWs = (status, obj) => {
		if (status !== COMM_OK) {
			// console.error('connMeasRxWs: error', status);

			if (gStorage.conn.connected) {
				console.error('connMeasRxWs: disconnecting!');
				gStorage.conn.ws.close();
			}

			gStorage.conn.setConnDelay('--');
			setTimeout(connMeasDelay, 1000);
			return;
		}

		if (obj.cmd === 'conn' && obj.op === 'rtt') {
			/* console.log('connStartTime', gStorage.conn.meas_start_time); */
			gStorage.conn.setConnDelay(Date.now() - gStorage.conn.meas_start_time);
		} else {
			console.error('connMeasRxWs: unknown command', obj.cmd, obj.op);
		}

		setTimeout(connMeasDelay, 1000);
	}

	const connMeasDelay = () => {
		try {
			if (document.hidden) {
				setTimeout(connMeasDelay, 1000);
				return;
			}

			const msg = {
				sess_id: gStorage.conn.sess_id,
				cmd: 'conn',
				op: 'rtt',
				data: {
				},
			};

			gStorage.conn.startConnMeas();
			// console.log('connMeasDelay, Tx!'); 
			gStorage.conn.ws.send(msg, connMeasRxWs);
		} catch (e) {
			console.error('connmeasdelay exception', e);
		}
	}

	/* ------------------------------------------------------------------ */

	function init() {
		let _ws;

		if (window.location.protocol === 'https:') {
			_ws = new WsR2('wss://' + window.location.hostname + '/ws', 'obs');
		} else {
			_ws = new WsR2('ws://' + window.location.hostname + '/ws', 'obs');
		}

		_ws.onopen(() => {
			gStorage.conn.setConnected(true);
			initialLogin(_ws);
		});

		_ws.onreopen(() => {
			gStorage.conn.setConnected(true);
			initialLogin(_ws);
		});

		_ws.onmessage((status, msg) => {
			if (msg.cmd === 'logout') {
				handleLogout(msg);
			} else if (msg.cmd === 'signal') {
				if (msg.op === 'new_notification') {
					gStorage.conn.setNewNotification(msg.data.new_notification ? true : false);
				}
			}
		});

		_ws.onclose(() => {
// 			console.log('WebSocket Close!');
			gStorage.conn.setConnected(false);
			if (gStorage.conn.online) {
				gStorage.conn.setOnline(false, false);
			}
		});

		_ws.onprotoerror((msg) => {
// 			console.log('onprotoerror', msg);

			if (msg && msg.data) {
				if (msg.data.error === OE_AUTH) {
					console.error('OnProtoError: authentication error!');

					gStorage.conn.setAuth(false);
					window.localStorage.removeItem('sess_id');
				} else {
					console.error('OnProtoError:', msg.data.error, msg.data.desc);
				}
			}
		});

		gStorage.conn.setWs(_ws);

		/* ---------------------------------------------------------- */

		if ('geolocation' in navigator) {
			const gpsID = navigator.geolocation.watchPosition(
				refreshGps,
				gpsErr,
				{enableHighAccuracy: true, timeout: 5000, maximumAge: 1000, distanceFilter: 10}
			);

			gStorage.gps.setAvailable(true);
			gStorage.gps.setID(gpsID);
		} else {
			gStorage.gps.setAvailable(false);
		}

		/* ---------------------------------------------------------- */

		if ('SpeechRecognition' in window) {
			const SpeechRecognition = window.SpeechRecognition;
			const rec = new SpeechRecognition();
			rec.abort();

			gStorage.speechRec.setRecognizer(rec);
		} else {
			console.log('Speech recognition is not available!');
			gStorage.speechRec.setRecognizer(null);
		}

		/* ---------------------------------------------------------- */

		const lang = window.localStorage.getItem('language');
		if (lang !== 'fi' && lang !== 'en') {
			i18n.changeLanguage('en');
		} else {
			i18n.changeLanguage(lang);
		}

		/* ---------------------------------------------------------- */

		let forcedOffline = false;

		const offlineString = window.localStorage.getItem('forcedOffline');
		if (offlineString) 
			forcedOffline = JSON.parse(offlineString);

		gStorage.conn.setForceOffline(forcedOffline);

		/* ---------------------------------------------------------- */

		initLists();

		 if (navigator.connection) {
			gStorage.conn.setConnLevel(navigator.connection.downlink * 100 / 10);
			navigator.connection.onchange = onNetworkConnectionChanged;
		}

		if (process.env.NODE_ENV === 'production') {
			setTimeout(connMeasDelay, 1000);
		}

		setInitDone(true);
	}

	/* ------------------------------------------------------------------ */

	function refreshGps(pos) {
		gStorage.gps.setGpsPos({latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy});
		gStorage.gps.setAvailable(true);
	}

	function gpsErr(error) {
		console.error('gpsErr', error);
		gStorage.gps.setAvailable(false);
	}

	/* ------------------------------------------------------------------ */
	
	function onNetworkConnectionChanged() {
		/* console.log('nNetworkConnectionChanged, navigator.connection', navigator.connection); */
		gStorage.conn.setConnLevel(navigator.connection.downlink * 100 / 10);
	}

	/* ------------------------------------------------------------------ */

	function handle_info(status, msg) {
		if (status !== COMM_OK) {
			console.error('app: handle_info: comm error ',  + status);
			return -1;
		}

		if (msg.op === 'get') {
			if (msg.result === 'OK') {
//  				console.log('Info get: ', msg.data);
				gStorage.srv.setInfo(msg.data);
			} else {
				console.error('app: handle_info: load failed ', msg.data.desc);
			}
		} else {
			console.error('app: handle_info: invalid operation ', msg.op);
		}
	}

	function handleLogin(status, msg) {
		if (status === COMM_OK) {
			if (msg.result === 'OK') {
// 				console.log('handleLogin', msg.data.user);

// 				if (msg.data.user.pwd_reset_req) {
// 					console.log('Password change needed for', msg.data.user.nick);
// 				}

				gStorage.conn.setUser(msg.data.user);
				gStorage.conn.setSessId(msg.sess_id);
				gStorage.conn.setNewNotification(msg.new_notifications ? true : false);

				if (!gStorage.splash.loadDone && !gStorage.conn.forcedOffline)
					gStorage.splash.setLoading(true);

				window.localStorage.setItem('sess_id', msg.sess_id);
				window.localStorage.setItem('user', JSON.stringify(msg.data.user));
				gStorage.conn.setAuth(true);

				gStorage.main.setForceLoad(true);
			} else {
				gStorage.conn.setAuth(false);
				gStorage.conn.setNewNotification(false);
				gStorage.splash.setLoading(false);

				window.localStorage.removeItem('sess_id');

				/* Ask info (makes it possible to show server version in about-dialog)! */
				let msg = {
					cmd: 'info',
					op: 'get',
				}

				gStorage.conn.ws.send(msg, handle_info);
			}

			gStorage.conn.setInitialLoginDone(true);
			if (!gStorage.conn.forcedOffline)
				gStorage.conn.setOnline(true, false);
		} else {
			console.error('handleLogin: status', status);
		}
	}

	function initialLogin(ws) {
		const sess_id = window.localStorage.getItem('sess_id');
		if (sess_id) {
			const msg = {
				sess_id: sess_id,
				cmd: 'login',
				data :{
					user: '',
					passwd: ''
				}
			}

			ws.send(msg, handleLogin);
		} else {
			/* Ask info (makes it possible to show server version in about-dialog)! */
			let msg = {
				cmd: 'info',
		     		op: 'get',
			}

			ws.send(msg, handle_info);
		}
	}

	function handleLogout(msg) {
		gStorage.conn.setAuth(false);
		gStorage.conn.setNewNotification(false);
		window.localStorage.removeItem('sess_id');
	}

	/* ------------------------------------------------------------------ */

	function endLoading(n_load) {
		if (n_load) {
			initLists();
		}
		gStorage.splash.setLoading(false);
		gStorage.splash.setLoadDone(true);

// 		console.log('App.endLoading', n_load, gStorage.lists.userlist);
	}

	function forcedPasswordChanged() {
// 		console.log('App: passwdChanged');
	}

	function volunteerPasswordChanged() {
		navigate('/');
	}

	/* ------------------------------------------------------------------ */

	// console.log('window.location.pathname', window.location.pathname, gStorage.conn.is_auth);

	if (!gStorage.conn.is_auth) {
		let sess_id = window.localStorage.getItem('sess_id');
		let user = null;

		try {
			user = JSON.parse(window.localStorage.getItem('user'));
		} catch {
			user = null;
		};

		// console.log('user, session:', user, sess_id, gStorage.conn.connected);

		if (!sess_id || !sess_id.length || !user) {
			// console.log('App, location', window.location.pathname);
		} else if (gStorage.conn.connected) {
			initialLogin(gStorage.conn.ws);
		} else {
			gStorage.conn.setUser(user);

			if (!gStorage.lists.userlist || !gStorage.lists.userlist.list || gStorage.lists.userlist.list.length <= 0)
				gStorage.lists.setUserList({last_upd: unixNow(), list: [user]});

			gStorage.conn.setAuth(true);
		}
	}

	if (gStorage.conn.user && gStorage.conn.user.pwd_reset_req && gStorage.conn.connected) {
		return (<PwdDialog set_open={true} onClose={forcedPasswordChanged} forced={true}/>);
	}

	if (gStorage.splash.loading)
		return (<LoadSplash endLoading={endLoading}/>);

	if (!initDone) {
		// console.log('init not done!');
		return null;
	}

	return (
		<Routes>
			<Route path='/tt' element={<BWTF/>} />
			<Route path='/login' element={<Login handleLogin={handleLogin} />} />
			<Route path='/about' element={<AboutDialog/>} />

			<Route element={<ProtectedRoute />} >
				<Route path='/' 		element={<ObsMain />} />
				<Route path='/statistics'	element={<Stats />} />
				<Route path='/add'		element={<ObsAdd />} />
				<Route path='/taxons'		element={<Taxons />} />
				<Route path='/filter'		element={<SearchDialog />} />
				<Route path='/navi'		element={<Navi />} />
				<Route path='/map'		element={<ListMap />} />
				<Route path='/settings'		element={<Settings />} />
				<Route path='/pwd'		element={<PwdDialog set_open={true} onClose={volunteerPasswordChanged} forced={false} />} />
				<Route path='/calendar'		element={<Calendar />} />
				<Route path='/notification_list' element={<NotificationList />} />
				<Route path='/notifications'	element={<Notifications />} />
				<Route path='/destlist'		element={<DestList />} />
				<Route path='/locations'	element={<Locations />} />
				<Route path='/trips'		element={<TripList />} />
				<Route path='/users'		element={<UserList />} />
				<Route path="*" 		element={<ObsMain />} />
			</Route>
		</Routes>
	);

});

export default App;
